import { useToast } from '@insio/ui-toolkit';

const DEFAULT_DURATIONS = {
  SUCCESS: 2000,
  ERROR: 4000,
  WARNING: 3500,
  INFO: 3000
} as const;

/**
 * Convenience wrappers around `useToast` for common notification scenarios.
 *
 * Each helper takes only a `message` string and applies the corresponding
 * semantic type + a sensible default duration — eliminating repetitive
 * `{ type, message, duration }` boilerplate at call sites.
 *
 * @example
 * const { showSuccess, showError } = useToastHelpers();
 * showSuccess(t('saved'));
 * showError(t('saveFailed'));
 */
export function useToastHelpers() {
  const { show } = useToast();

  const showSuccess = (message: string) =>
    show({ type: 'success', message, duration: DEFAULT_DURATIONS.SUCCESS });

  const showError = (message: string) =>
    show({ type: 'error', message, duration: DEFAULT_DURATIONS.ERROR });

  const showWarning = (message: string) =>
    show({ type: 'warning', message, duration: DEFAULT_DURATIONS.WARNING });

  const showInfo = (message: string) =>
    show({ type: 'info', message, duration: DEFAULT_DURATIONS.INFO });

  return { showSuccess, showError, showWarning, showInfo };
}
